/**
 * QuestionnaireTestSuite
 *
 * Dev-only tool for inspecting questionnaire structure, weights,
 * and generating test assessments to preview scoring & reports.
 */

import {
  ChevronDown,
  ChevronRight,
  Dices,
  Layers,
  Play,
  RotateCcw,
  SlidersHorizontal,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  questionnaireService,
  type QuestionnaireStructure,
  type QuestionnaireQuestion,
  type AssessmentResponse,
} from "@/modules/questionnaire";
import { ResultsSummary } from "@/modules/questionnaire/components/ResultsSummary";
import { SCORE_LEVELS, LAYER_COLORS } from "@/modules/questionnaire/constants";
import {
  calculateOverallScore,
  type OverallScore,
} from "@/modules/questionnaire/utils/scoring.utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ScoreProfile =
  | "excellent"
  | "good"
  | "moderate"
  | "low"
  | "critical"
  | "random"
  | "custom";

interface ProfileConfig {
  label: string;
  description: string;
  value: number | null; // null = random
  color: string;
}

const PROFILES: Record<ScoreProfile, ProfileConfig> = {
  excellent: {
    label: "All 5s",
    description: "Excellent (85-100)",
    value: 5,
    color: "#059669",
  },
  good: {
    label: "All 4s",
    description: "Good (70-84)",
    value: 4,
    color: "#10B981",
  },
  moderate: {
    label: "All 3s",
    description: "Moderate (55-69)",
    value: 3,
    color: "#EAB308",
  },
  low: {
    label: "All 2s",
    description: "Low (40-54)",
    value: 2,
    color: "#F59E0B",
  },
  critical: {
    label: "All 1s",
    description: "Critical (0-39)",
    value: 1,
    color: "#DC2626",
  },
  random: {
    label: "Random",
    description: "Random 1-5",
    value: null,
    color: "#6366F1",
  },
  custom: {
    label: "Custom",
    description: "Set a value",
    value: 3,
    color: "#8B5CF6",
  },
};

// ---------------------------------------------------------------------------
// Helper: generate responses for a given profile
// ---------------------------------------------------------------------------

function generateResponses(
  questions: QuestionnaireQuestion[],
  profile: ScoreProfile,
  customValue: number,
): AssessmentResponse[] {
  return questions.map((q) => {
    let value: number;
    if (profile === "random") {
      value = Math.floor(Math.random() * 5) + 1;
    } else if (profile === "custom") {
      value = customValue;
    } else {
      value = PROFILES[profile].value!;
    }
    return { question_id: q.id, response_value: value };
  });
}

// ---------------------------------------------------------------------------
// Sub-component: Weight bar
// ---------------------------------------------------------------------------

function WeightBar({ weight, color }: { weight: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 bg-gray-200 rounded-full h-2">
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${weight}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs font-mono text-gray-500 w-10 text-right">
        {weight}%
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Structure Inspector
// ---------------------------------------------------------------------------

function StructureInspector({
  structure,
}: {
  structure: QuestionnaireStructure;
}) {
  const [expandedLayers, setExpandedLayers] = useState<Set<string>>(
    new Set(structure.layers.map((l) => l.id)),
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );

  const toggleLayer = (id: string) => {
    setExpandedLayers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCategory = (id: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getCategoriesForLayer = (layerId: string) =>
    structure.categories
      .filter((c) => c.layer_id === layerId)
      .sort((a, b) => a.display_order - b.display_order);

  const getQuestionsForCategory = (categoryId: string) =>
    structure.questions
      .filter((q) => q.category_id === categoryId)
      .sort((a, b) => a.display_order - b.display_order);

  // Compute total weight per layer to show if they sum correctly
  const layerWeightSum = structure.layers.reduce(
    (sum, l) => sum + l.weight_percentage,
    0,
  );

  return (
    <div className="space-y-4">
      {/* Version info */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm text-gray-500">Active Version</span>
            <h3 className="font-semibold text-gray-900">
              {structure.version.name} (v{structure.version.version_number})
            </h3>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">
              {structure.questions.length} questions across{" "}
              {structure.categories.length} categories in{" "}
              {structure.layers.length} layers
            </div>
            <div
              className={`text-xs font-mono mt-1 ${layerWeightSum === 100 ? "text-green-600" : "text-red-600"}`}
            >
              Layer weights sum: {layerWeightSum}%
              {layerWeightSum !== 100 && " (should be 100%)"}
            </div>
          </div>
        </div>
      </div>

      {/* Layer tree */}
      {structure.layers
        .sort((a, b) => a.display_order - b.display_order)
        .map((layer) => {
          const layerColor =
            (LAYER_COLORS as Record<string, string>)[layer.name] || "#3B82F6";
          const cats = getCategoriesForLayer(layer.id);
          const catWeightSum = cats.reduce(
            (s, c) => s + c.weight_percentage,
            0,
          );
          const isExpanded = expandedLayers.has(layer.id);

          return (
            <div
              key={layer.id}
              className="border rounded-lg overflow-hidden"
              style={{ borderColor: `${layerColor}40` }}
            >
              {/* Layer header */}
              <button
                onClick={() => toggleLayer(layer.id)}
                className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
                style={{ backgroundColor: `${layerColor}08` }}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                )}
                <Layers
                  className="w-5 h-5 flex-shrink-0"
                  style={{ color: layerColor }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900">
                    Layer {layer.layer_number}: {layer.name}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {layer.description}
                  </div>
                </div>
                <WeightBar
                  weight={layer.weight_percentage}
                  color={layerColor}
                />
              </button>

              {/* Categories */}
              {isExpanded && (
                <div className="border-t border-gray-100">
                  {/* Category weight health */}
                  <div className="px-4 py-2 bg-gray-50 text-xs flex items-center justify-between">
                    <span className="text-gray-500">
                      {cats.length} categories
                    </span>
                    <span
                      className={`font-mono ${catWeightSum === 100 ? "text-green-600" : "text-red-600"}`}
                    >
                      Category weights sum: {catWeightSum}%
                      {catWeightSum !== 100 && " (should be 100%)"}
                    </span>
                  </div>

                  {cats.map((category) => {
                    const questions = getQuestionsForCategory(category.id);
                    const isCatExpanded = expandedCategories.has(category.id);

                    return (
                      <div
                        key={category.id}
                        className="border-t border-gray-100"
                      >
                        {/* Category header */}
                        <button
                          onClick={() => toggleCategory(category.id)}
                          className="w-full flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors text-left"
                        >
                          {isCatExpanded ? (
                            <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-800">
                              {category.category_number}. {category.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {questions.length} questions
                            </div>
                          </div>
                          <WeightBar
                            weight={category.weight_percentage}
                            color={layerColor}
                          />
                        </button>

                        {/* Questions */}
                        {isCatExpanded && (
                          <div className="bg-gray-50 px-8 py-2 space-y-2">
                            {questions.map((q, qi) => (
                              <div
                                key={q.id}
                                className="text-sm text-gray-700 py-1 flex items-start gap-2"
                              >
                                <span className="text-xs font-mono text-gray-400 mt-0.5 w-6 flex-shrink-0">
                                  Q{qi + 1}
                                </span>
                                <div>
                                  <div>{q.question_text}</div>
                                  {q.example_text && (
                                    <div className="text-xs text-gray-400 italic mt-0.5">
                                      e.g. {q.example_text}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Score Breakdown Table
// ---------------------------------------------------------------------------

function ScoreBreakdown({ results }: { results: OverallScore }) {
  const level = SCORE_LEVELS[results.level];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <h4 className="font-semibold text-gray-900">Score Breakdown</h4>

      {/* Overall */}
      <div className="flex items-center gap-3">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white"
          style={{ backgroundColor: level.color }}
        >
          {Math.round(results.total_score)}
        </div>
        <div>
          <div className="font-semibold" style={{ color: level.color }}>
            {level.label}
          </div>
          <div className="text-sm text-gray-500">{level.description}</div>
        </div>
      </div>

      {/* Layer & category table */}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-2 font-medium">Component</th>
            <th className="py-2 font-medium text-right">Score</th>
            <th className="py-2 font-medium text-right">Weight</th>
            <th className="py-2 font-medium text-right">Weighted</th>
          </tr>
        </thead>
        <tbody>
          {results.layers.map((layer) => (
            <React.Fragment key={layer.layer_id}>
              <tr className="border-b border-gray-100 font-medium">
                <td className="py-2">{layer.layer_name}</td>
                <td className="py-2 text-right">{layer.score.toFixed(1)}</td>
                <td className="py-2 text-right">{layer.weight}%</td>
                <td className="py-2 text-right font-semibold">
                  {layer.weighted_score.toFixed(1)}
                </td>
              </tr>
              {layer.categories.map((cat) => (
                <tr
                  key={cat.category_id}
                  className="text-gray-600 border-b border-gray-50"
                >
                  <td className="py-1.5 pl-4">{cat.category_name}</td>
                  <td className="py-1.5 text-right">{cat.score.toFixed(1)}</td>
                  <td className="py-1.5 text-right">{cat.weight}%</td>
                  <td className="py-1.5 text-right">
                    {cat.weighted_score.toFixed(1)}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
          <tr className="font-bold border-t-2 border-gray-300">
            <td className="py-2">Total</td>
            <td className="py-2 text-right">
              {results.total_score.toFixed(1)}
            </td>
            <td className="py-2 text-right">100%</td>
            <td className="py-2 text-right">
              {results.total_score.toFixed(1)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function QuestionnaireTestSuite() {
  // Questionnaire data
  const [structure, setStructure] = useState<QuestionnaireStructure | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Test generation state
  const [selectedProfile, setSelectedProfile] = useState<ScoreProfile | null>(
    null,
  );
  const [customValue, setCustomValue] = useState(3);
  const [generatedResponses, setGeneratedResponses] = useState<
    AssessmentResponse[] | null
  >(null);
  const [showReport, setShowReport] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState<"structure" | "generator">(
    "structure",
  );

  // Fetch questionnaire
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await questionnaireService.getActiveQuestionnaire();
        setStructure(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load questionnaire",
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Calculate scores
  const results: OverallScore | null = useMemo(() => {
    if (!structure || !generatedResponses) return null;
    return calculateOverallScore(
      structure.layers,
      structure.categories,
      structure.questions,
      generatedResponses,
    );
  }, [structure, generatedResponses]);

  // Generate test responses
  const handleGenerate = useCallback(
    (profile: ScoreProfile) => {
      if (!structure) return;
      setSelectedProfile(profile);
      const responses = generateResponses(
        structure.questions,
        profile,
        customValue,
      );
      setGeneratedResponses(responses);
      setShowReport(false);
    },
    [structure, customValue],
  );

  // Reset
  const handleReset = useCallback(() => {
    setSelectedProfile(null);
    setGeneratedResponses(null);
    setShowReport(false);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Error state
  if (error || !structure) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 font-medium mb-2">
          Failed to load questionnaire
        </p>
        <p className="text-sm text-red-600">{error}</p>
        <p className="text-sm text-gray-500 mt-3">
          Make sure the API is running and the questionnaire is seeded.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab("structure")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "structure"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <span className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Structure & Weights
          </span>
        </button>
        <button
          onClick={() => setActiveTab("generator")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "generator"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <span className="flex items-center gap-2">
            <Dices className="w-4 h-4" />
            Test Generator
          </span>
        </button>
      </div>

      {/* Structure tab */}
      {activeTab === "structure" && (
        <StructureInspector structure={structure} />
      )}

      {/* Generator tab */}
      {activeTab === "generator" && (
        <div className="space-y-6">
          {/* Profile buttons */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-1">
              Generate Test Assessment
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Click a profile to generate {structure.questions.length} responses
              and preview the scoring output.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(
                Object.entries(PROFILES) as [ScoreProfile, ProfileConfig][]
              ).map(([key, profile]) => {
                if (key === "custom") return null;
                const isSelected = selectedProfile === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleGenerate(key)}
                    className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? "border-current shadow-md"
                        : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                    }`}
                    style={
                      isSelected
                        ? { borderColor: profile.color, color: profile.color }
                        : undefined
                    }
                  >
                    {key === "random" ? (
                      <Dices
                        className="w-5 h-5 mb-2"
                        style={{ color: profile.color }}
                      />
                    ) : (
                      <Play
                        className="w-5 h-5 mb-2"
                        style={{ color: profile.color }}
                      />
                    )}
                    <div className="font-semibold text-gray-900 text-sm">
                      {profile.label}
                    </div>
                    <div className="text-xs text-gray-500">
                      {profile.description}
                    </div>
                  </button>
                );
              })}

              {/* Custom slider */}
              <div
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedProfile === "custom"
                    ? "border-purple-400 shadow-md"
                    : "border-gray-200"
                }`}
              >
                <SlidersHorizontal className="w-5 h-5 mb-2 text-purple-600" />
                <div className="font-semibold text-gray-900 text-sm mb-2">
                  Custom: All {customValue}s
                </div>
                <input
                  type="range"
                  min={1}
                  max={5}
                  step={1}
                  value={customValue}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setCustomValue(v);
                    handleGenerate("custom");
                  }}
                  className="w-full accent-purple-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                  <span>4</span>
                  <span>5</span>
                </div>
              </div>
            </div>

            {/* Reset */}
            {generatedResponses && (
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
                <button
                  onClick={() => setShowReport(!showReport)}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  {showReport ? "Hide" : "Show"} Full Report Preview
                </button>
                {selectedProfile === "random" && (
                  <button
                    onClick={() => handleGenerate("random")}
                    className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                  >
                    <Dices className="w-4 h-4" />
                    Re-roll
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Score breakdown */}
          {results && <ScoreBreakdown results={results} />}

          {/* Full report preview */}
          {showReport && generatedResponses && (
            <div className="bg-white rounded-lg border border-gray-200 p-2">
              <div className="text-xs text-gray-500 px-4 py-2 border-b border-gray-100 mb-2">
                Report Preview (as users would see it)
              </div>
              <ResultsSummary
                layers={structure.layers}
                categories={structure.categories}
                questions={structure.questions}
                responses={generatedResponses}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
